import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './App.css';

const getNotificationMessages = (locationParam) => [
  { text: `Family in ${locationParam} finally warm this winter`, icon: 'fa-house-chimney' },
  { text: 'Only 5 spots left this week', icon: 'fa-clock' },
  { text: `The Johnsons stopped their heat from escaping`, icon: 'fa-fire' },
  { text: 'Another family sleeping warm tonight', icon: 'fa-bed' },
  { text: `${locationParam} home now holds heat all night`, icon: 'fa-circle-check' },
  { text: 'Limited availability - 2 spots left', icon: 'fa-clock' }
];

const heroSlides = [
  {
    image: '/images/hero-1.jpg',
    title: 'Your Heat Is Escaping Right Now',
    subtitle: 'While you read this, warm air is leaking out of your home'
  },
  {
    image: '/images/hero-2.jpg',
    title: 'Stop Heating the Outdoors',
    subtitle: 'Poor insulation means you\'re paying to warm the neighborhood'
  },
  {
    image: '/images/hero-3.jpg',
    title: 'Your Family Deserves to Be Warm',
    subtitle: 'No more cold floors, drafty rooms, or shivering at night'
  }
];

function ReplacementPage() {
  const [searchParams] = useSearchParams();
  const locationParam = searchParams.get('location') || 'Denver';
  
  // Load saved form data from localStorage on mount
  const loadSavedFormData = () => {
    try {
      const saved = localStorage.getItem('levelup_form_data');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading saved form data:', e);
    }
    return {
      name: '',
      email: '',
      phone: '',
      address: '',
      issue: ''
    };
  };

  const [formData, setFormData] = useState(loadSavedFormData);
  const [submitted, setSubmitted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // heroSlides is a constant array, doesn't need to be in dependencies

  useEffect(() => {
    const notificationMessages = getNotificationMessages(locationParam);
    
    const showRandomNotification = () => {
      const randomMessage = notificationMessages[Math.floor(Math.random() * notificationMessages.length)];
      setNotificationMessage(randomMessage);
      setShowNotification(true);
      
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    };

    // Show first notification after 5 seconds
    const initialTimer = setTimeout(() => {
      showRandomNotification();
    }, 5000);

    // Then show every 30 seconds
    const interval = setInterval(() => {
      showRandomNotification();
    }, 30000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationParam]);

  // Save form data to localStorage and backend
  const saveFormData = async (data, isPartial = true) => {
    try {
      // Save to localStorage
      localStorage.setItem('levelup_form_data', JSON.stringify(data));
      
      // Save to backend file (if endpoint exists)
      try {
        const apiUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
        const response = await fetch(`${apiUrl}/api/save-form`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...data,
            location: locationParam,
            timestamp: new Date().toISOString(),
            isPartial: isPartial,
            url: window.location.href
          })
        });
        
        if (response.ok) {
          setLastSaved(new Date());
        }
      } catch (backendError) {
        // Backend might not be set up yet, that's okay
        console.log('Backend save not available:', backendError);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  };

  const handleChange = (e) => {
    const newFormData = {
      ...formData,
      [e.target.name]: e.target.value
    };
    setFormData(newFormData);
    
    // Auto-save after user stops typing (debounce)
    clearTimeout(window.formSaveTimeout);
    window.formSaveTimeout = setTimeout(() => {
      saveFormData(newFormData, true);
    }, 1000); // Save 1 second after user stops typing
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Save final submission
    await saveFormData(formData, false);
    
    // Clear localStorage after successful submission
    localStorage.removeItem('levelup_form_data');
    
    // Send to backend/CRM
    try {
      const apiUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
      const response = await fetch(`${apiUrl}/api/submit-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          location: locationParam,
          timestamp: new Date().toISOString(),
          isPartial: false,
          url: window.location.href
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        console.log('Form submitted successfully');
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      // Fallback: still mark as submitted and save locally
      console.error('Error submitting form:', error);
      setSubmitted(true);
      
      // Save to localStorage as backup
      localStorage.setItem('levelup_pending_submissions', JSON.stringify([
        ...(JSON.parse(localStorage.getItem('levelup_pending_submissions') || '[]')),
        {
          ...formData,
          location: locationParam,
          timestamp: new Date().toISOString()
        }
      ]));
    }
  };

  return (
    <div className="App">
      {/* Header with Logo */}
      <header className="site-header">
        <div className="container">
          <div className="header-content">
            <img 
              src="/images/logo.png" 
              alt="Level Up Insulation" 
              className="logo"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="header-location">
              Serving {locationParam}
            </div>
            <a href="tel:+17204384271" className="btn header-call-btn">
              <i className="fa-solid fa-phone"></i> Call
            </a>
            <a href="#form" className="btn btn-primary header-cta-btn">
              Free Estimate
            </a>
          </div>
        </div>
      </header>

      {/* Notification Popup */}
      {showNotification && (
        <div className="notification-popup">
          <div className="notification-content">
            <span className="notification-icon"><i className={`fa-solid ${notificationMessage.icon}`}></i></span>
            <span className="notification-text">{notificationMessage.text}</span>
            <button 
              className="notification-close"
              onClick={() => setShowNotification(false)}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Hero Slider Section */}
      <section className="hero-slider">
        <div className="hero-slides">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="hero-overlay"></div>
            </div>
          ))}
        </div>
        <div className="hero-content">
          <div className="container">
            <div className="badge"><i className="fa-solid fa-fire"></i> YOUR HEAT IS ESCAPING</div>
            <h1 className="hero-title">
              <span className="hero-title-thin">Tired of Freezing</span> in Your Own Home?
            </h1>
            <p className="hero-subtitle">
              Right now, warm air is escaping through your walls and attic. Your furnace runs constantly, 
              but you're still cold. Your kids wear sweaters indoors. <strong>It doesn't have to be this way.</strong>
            </p>
            
            {/* Heat Loss Visual */}
            <div className="hero-heat-loss">
              <div className="heat-loss-stat">
                <span className="heat-icon"><i className="fa-solid fa-temperature-arrow-up"></i></span>
                <span className="heat-number">40%</span>
                <span className="heat-label">of your heat escapes through poor insulation</span>
              </div>
            </div>
            
            {/* Early Trust Signals */}
            <div className="hero-trust-bar">
              <div className="trust-item">
                <span className="trust-icon"><i className="fa-solid fa-location-dot"></i></span>
                <span>Local {locationParam} Team</span>
              </div>
              <div className="trust-item">
                <span className="trust-icon"><i className="fa-solid fa-star"></i></span>
                <span>312+ Warm Homes</span>
              </div>
              <div className="trust-item">
                <span className="trust-icon"><i className="fa-solid fa-shield-halved"></i></span>
                <span>Comfort Guaranteed</span>
              </div>
            </div>
            
            <div className="hero-cta">
              <a href="#form" className="btn btn-primary btn-emotional">
                Yes! I'm Ready to Be Warm →
              </a>
              <p className="hero-guarantee">✓ Free home assessment • ✓ Find your heat leaks • ✓ 7-day installation</p>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <div className="stat-number">Free</div>
                <div className="stat-label">Heat Loss Assessment</div>
              </div>
              <div className="stat">
                <div className="stat-number">7 Days</div>
                <div className="stat-label">To a Warmer Home</div>
              </div>
              <div className="stat">
                <div className="stat-number">100%</div>
                <div className="stat-label">Comfort Guarantee</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-dots">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="social-proof">
        <div className="container">
          <div className="social-proof-content">
            <div className="stars">
              <span className="star"><i className="fa-solid fa-star"></i></span>
              <span className="star"><i className="fa-solid fa-star"></i></span>
              <span className="star"><i className="fa-solid fa-star"></i></span>
              <span className="star"><i className="fa-solid fa-star"></i></span>
              <span className="star"><i className="fa-solid fa-star"></i></span>
            </div>
            <p className="social-proof-text">
              <strong>312+ {locationParam} families</strong> are finally warm this winter
            </p>
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <section 
        className="pain-points problem"
        style={{
          backgroundImage: 'url(/images/work-1.jpg)'
        }}
      >
        <div className="shape-bottom"></div>
        <div className="container">
          <h2 className="section-title">Is This Happening in Your Home?</h2>
          <p className="section-subtitle">
            Every winter, {locationParam} families suffer through these problems. Sound familiar?
          </p>
          <div className="pain-grid">
            <div className="pain-item">
              <div className="pain-icon"><i className="fa-solid fa-snowflake"></i></div>
              <h3>Waking Up Freezing</h3>
              <p>You dread getting out of bed because the house is so cold. The floors are like ice. Your kids complain every morning.</p>
            </div>
            <div className="pain-item">
              <div className="pain-icon"><i className="fa-solid fa-file-invoice-dollar"></i></div>
              <h3>Dreading the Energy Bill</h3>
              <p>You're scared to open the envelope. $400... $500... Where does it end? You're paying a fortune but still shivering.</p>
            </div>
            <div className="pain-item">
              <div className="pain-icon"><i className="fa-solid fa-temperature-low"></i></div>
              <h3>That One Room That's Always Cold</h3>
              <p>No matter what you do, it's freezing. You've given up using it in winter. It's like a refrigerator in your own home.</p>
            </div>
            <div className="pain-item">
              <div className="pain-icon"><i className="fa-solid fa-wind"></i></div>
              <h3>Feeling Drafts Everywhere</h3>
              <p>Cold air seeping in around windows, through walls. You can literally feel your expensive heat escaping.</p>
            </div>
            <div className="pain-item">
              <div className="pain-icon"><i className="fa-solid fa-hand-holding-dollar"></i></div>
              <h3>Guilty About Turning Up Heat</h3>
              <p>You want to be warm, but every degree costs more money you don't have. So you layer up and suffer in silence.</p>
            </div>
          </div>
          <div className="pain-cta">
            <p className="pain-message">
              <strong>You shouldn't have to choose between comfort and your bank account.</strong> 
              Your family deserves to be warm without going broke. Let us show you how.
            </p>
            <a href="#form" className="btn btn-primary btn-emotional">
              Yes! I Want My Family to Be Warm →
            </a>
          </div>
        </div>
      </section>

      {/* Insulation Types Section */}
      <section className="insulation-types">
        <div className="container">
          <h2 className="section-title">Insulation Options Available</h2>
          <p className="section-subtitle-light">
            We install all types of insulation to match your home's needs and budget
          </p>
          <div className="types-grid">
            <div className="type-card type-popular">
              <div className="type-badge">Most Popular</div>
              <div className="type-icon"><i className="fa-solid fa-fan"></i></div>
              <h3>Blown-In Cellulose</h3>
              <p>Most common choice for attics. Eco-friendly, cost-effective, and excellent thermal performance.</p>
            </div>
            <div className="type-card">
              <div className="type-icon"><i className="fa-solid fa-layer-group"></i></div>
              <h3>Fiberglass Batts</h3>
              <p>Traditional rolls or batts. Affordable and widely used for walls and attics.</p>
            </div>
            <div className="type-card">
              <div className="type-icon"><i className="fa-solid fa-cubes"></i></div>
              <h3>Mineral Wool</h3>
              <p>Premium option with superior fire resistance and soundproofing qualities.</p>
            </div>
            <div className="type-card type-premium">
              <div className="type-badge">Best Performance</div>
              <div className="type-icon"><i className="fa-solid fa-spray-can"></i></div>
              <h3>Spray Foam</h3>
              <p>Open or closed cell options. Closed cell provides the highest R-value and air sealing.</p>
            </div>
            <div className="type-card">
              <div className="type-icon"><i className="fa-solid fa-square"></i></div>
              <h3>Rigid Board</h3>
              <p>High R-value per inch. Ideal for basements, foundations, and exterior applications.</p>
            </div>
            <div className="type-card">
              <div className="type-icon"><i className="fa-solid fa-droplet-slash"></i></div>
              <h3>Vapor Barrier</h3>
              <p>Essential for crawlspaces, basements, and garages. Controls moisture and protects your home.</p>
            </div>
          </div>
          
          {/* Financing Available */}
          <div className="financing-banner">
            <div className="financing-content">
              <div className="financing-icon"><i className="fa-solid fa-credit-card"></i></div>
              <div className="financing-text">
                <h3>Financing Available</h3>
                <p>Affordable monthly payments to fit your budget. Get the insulation you need now, pay over time.</p>
              </div>
              <a href="#form" className="btn btn-primary btn-emotional">
                Check My Options →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Work Showcase Section */}
      <section className="work-showcase">
        <div className="container">
          <h2 className="section-title">Our Replacement Process</h2>
          <p className="section-subtitle">
            See our expert team in action, delivering professional insulation replacement across {locationParam}
          </p>
          <div className="showcase-grid">
            <div className="showcase-item">
              <div className="showcase-image-wrapper">
                <img 
                  src="/images/work-1.jpg" 
                  alt="Professional insulation removal" 
                  className="showcase-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="showcase-content">
                <h3>Safe Removal</h3>
                <p>Professional removal of old, damaged, or contaminated insulation with proper safety protocols</p>
              </div>
            </div>
            <div className="showcase-item">
              <div className="showcase-image-wrapper">
                <img 
                  src="/images/work-2.jpg" 
                  alt="Expert consultation" 
                  className="showcase-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="showcase-content">
                <h3>Expert Assessment</h3>
                <p>Thorough evaluation to determine the best replacement materials for your home</p>
              </div>
            </div>
            <div className="showcase-item">
              <div className="showcase-image-wrapper">
                <img 
                  src="/images/work-3.jpg" 
                  alt="Advanced replacement technology" 
                  className="showcase-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="showcase-content">
                <h3>Modern Installation</h3>
                <p>Installation of high-performance insulation materials using advanced techniques</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <div className="section-divider"></div>
      <section className="solution">
        <div className="container">
          <h2 className="section-title">Why Replace Your Insulation?</h2>
          <p className="section-subtitle">
            Old or damaged insulation can cost you hundreds in wasted energy. Modern materials 
            provide better performance and significant savings.
          </p>
          <div className="solution-wrapper">
            <div className="solution-grid">
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-2.jpg" alt="Free consultation" className="solution-image" />
                <div className="solution-number">01</div>
              </div>
              <div className="solution-content">
                <h3>Free Consultation & Assessment</h3>
                <p>Expert evaluation of your current insulation and recommendations for replacement</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-4.jpg" alt="Professional removal" className="solution-image" />
                <div className="solution-number">02</div>
              </div>
              <div className="solution-content">
                <h3>Safe Professional Removal</h3>
                <p>Complete removal of old insulation with proper safety and disposal protocols</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-3.jpg" alt="Modern materials" className="solution-image" />
                <div className="solution-number">03</div>
              </div>
              <div className="solution-content">
                <h3>Modern High-Performance Materials</h3>
                <p>Installation of the latest insulation technology for maximum energy efficiency</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-1.jpg" alt="Energy savings" className="solution-image" />
                <div className="solution-number">04</div>
              </div>
              <div className="solution-content">
                <h3>Significant Energy Savings</h3>
                <p>Reduce your energy bills by up to 30% with properly installed modern insulation</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-5.jpg" alt="Installation guarantee" className="solution-image" />
                <div className="solution-number">05</div>
              </div>
              <div className="solution-content">
                <h3>7-Day Installation Guarantee</h3>
                <p>Replacement starts within 7 days of consultation, or receive $300 credit</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-6.jpg" alt="Warranty" className="solution-image" />
                <div className="solution-number">06</div>
              </div>
              <div className="solution-content">
                <h3>Warranty & Quality Guarantee</h3>
                <p>All work backed by comprehensive warranty and quality assurance</p>
              </div>
            </div>
            </div>
            <div className="solution-customer-image">
              <img 
                src="/images/customer-1.jpg" 
                alt="Happy customer" 
                className="customer-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="customer-name">
                <p className="customer-name-text">Joanne Roarke</p>
                <p className="customer-label">"Finally warm after 10 years!"</p>
              </div>
              <a href="#form" className="btn btn-primary customer-cta-btn">
                Get Your Free Estimate →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Form Section */}
      <section id="form" className="form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-content">
              <h2>Find Out Where Your Heat Is Escaping</h2>
              <p className="form-subtitle">
                Get a free heat loss assessment. We'll show you exactly where warm air is leaking out, and how to stop it.
              </p>
              
              {/* Capacity Progress Bar */}
              <div className="capacity-progress">
                <div className="capacity-header">
                  <span className="capacity-label"><i className="fa-solid fa-snowflake"></i> Families Waiting for Warmth</span>
                  <span className="capacity-percentage">85%</span>
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: '85%' }}>
                    <div className="progress-bar-fill"></div>
                  </div>
                </div>
                <p className="capacity-message">Only a few assessment spots left this week!</p>
              </div>
              {!submitted ? (
                <form onSubmit={handleSubmit} className="lead-form">
                  {lastSaved && (
                    <div className="form-save-indicator">
                      <span className="save-icon"><i className="fa-solid fa-floppy-disk"></i></span>
                      <span>Form auto-saved</span>
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(303) 555-1234"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="address">Property Address *</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      placeholder={`123 Main St, ${locationParam}, CO`}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="issue">What's your main concern? (Optional)</label>
                    <select
                      id="issue"
                      name="issue"
                      value={formData.issue}
                      onChange={handleChange}
                    >
                      <option value="">What bothers you most?</option>
                      <option value="cold-rooms">Rooms that won't stay warm</option>
                      <option value="high-bills">Energy bills are too high</option>
                      <option value="drafts">Feeling drafts and cold air</option>
                      <option value="cold-floors">Cold floors in winter</option>
                      <option value="furnace">Furnace runs constantly</option>
                      <option value="other">Something else</option>
                    </select>
                  </div>
                  <button type="submit" className="btn btn-primary btn-large btn-emotional">
                    Yes! Show Me Where My Heat Is Escaping →
                  </button>
                  <p className="form-disclaimer">
                    Free assessment, no obligation. We'll call within 5 minutes to schedule.
                  </p>
                </form>
              ) : (
                <div className="form-success">
                  <div className="success-icon">✓</div>
                  <h3>You're One Step Closer to a Warm Home!</h3>
                  <p>We've received your request. Our team will call you within 5 minutes to schedule your free heat loss assessment.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="guarantee">
        <div className="container">
          <div className="guarantee-content">
            <div className="guarantee-badge">OUR PROMISE TO YOU</div>
            <h2>You'll Be Warm in 7 Days, Guaranteed</h2>
            <p className="guarantee-text">
              We know you've been cold long enough. That's why we guarantee your insulation 
              upgrade will begin within 7 days. If we can't get you warmer within a week, 
              you'll receive a $300 credit. No excuses.
            </p>
            <p className="guarantee-disclaimer">*Terms and conditions apply. Availability varies by location.</p>
            <div className="guarantee-features">
              <div className="guarantee-feature">
                <span className="check-icon">✓</span>
                <span>We call you within 5 minutes</span>
              </div>
              <div className="guarantee-feature">
                <span className="check-icon">✓</span>
                <span>Assessment scheduled same day</span>
              </div>
              <div className="guarantee-feature">
                <span className="check-icon">✓</span>
                <span>You're warmer within 7 days, guaranteed</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="different">
        <div className="container">
          <h2 className="section-title">Why Level Up Insulation is Different</h2>
          <div className="different-grid">
            <div className="different-item">
              <h3>Complete Removal & Replacement</h3>
              <p>We handle both removal of old insulation and installation of new materials. Most competitors only do one</p>
            </div>
            <div className="different-item">
              <h3>Safe Disposal Protocols</h3>
              <p>Proper handling and disposal of damaged or contaminated insulation materials</p>
            </div>
            <div className="different-item">
              <h3>Modern Materials Only</h3>
              <p>We only install the latest, highest-performance insulation materials available</p>
            </div>
            <div className="different-item">
              <h3>Installation Time Guarantee</h3>
              <p>We're the only ones offering a 7-day installation guarantee with $300 credit</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            <div className="faq-item">
              <h3>Is the consultation really free?</h3>
              <p>Yes, absolutely. The assessment and consultation are completely free with no obligation to purchase anything.</p>
            </div>
            <div className="faq-item">
              <h3>How long does the replacement process take?</h3>
              <p>Most insulation replacement projects take 1-2 days, depending on the size of your home and scope of work.</p>
            </div>
            <div className="faq-item">
              <h3>What happens to the old insulation?</h3>
              <p>We safely remove and properly dispose of all old insulation according to local regulations and environmental standards.</p>
            </div>
            <div className="faq-item">
              <h3>Do I have to book replacement after the consultation?</h3>
              <p>No. There's no obligation. You'll receive your assessment and can decide when you're ready to proceed with replacement.</p>
            </div>
            <div className="faq-item">
              <h3>What if you can't start replacement within 7 days?</h3>
              <p>If we can't begin replacement within 7 days of your consultation, you'll receive a $300 credit toward your replacement.</p>
            </div>
            <div className="faq-item">
              <h3>Do you service my area?</h3>
              <p>We service {locationParam} and surrounding suburbs. Enter your address in the form above and we'll confirm availability.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="section-title">Serving Denver & Surrounding Areas</h2>
          <p className="section-subtitle">
            We provide professional insulation replacement services throughout the Denver metro area
          </p>
          <div className="map-wrapper">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d196281.1291475455!2d-105.1099275!3d39.7392358!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876b80aa231f17aa%3A0x620436728b3a4514!2sDenver%2C%20CO!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: '16px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Denver Service Area Map"
            ></iframe>
          </div>
          <div className="service-areas">
            <h3>Service Areas</h3>
            <div className="areas-grid">
              <div className="area-item">Denver</div>
              <div className="area-item">Aurora</div>
              <div className="area-item">Lakewood</div>
              <div className="area-item">Westminster</div>
              <div className="area-item">Arvada</div>
              <div className="area-item">Thornton</div>
              <div className="area-item">Centennial</div>
              <div className="area-item">Broomfield</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section 
        className="final-cta"
        style={{
          backgroundImage: 'url(/images/work-1.jpg)'
        }}
      >
        <div className="final-cta-overlay"></div>
        <div className="container">
          <h2>How Many More Cold Nights Will You Suffer Through?</h2>
          <p>Every day you wait is another day shivering, another high bill, another morning dreading the cold floors. Your family deserves better.</p>
          <a href="#form" className="btn btn-primary btn-large btn-emotional">
            Yes! I'm Ready for My Family to Be Warm →
          </a>
          <p className="final-cta-note">Free assessment • No obligation • Be warm within 7 days</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>Level Up Insulation</h3>
              <p>{locationParam}'s trusted insulation experts</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Services</h4>
                <a href="#form">Insulation Replacement</a>
                <a href="#form">Insulation Removal</a>
                <a href="#form">Free Consultation</a>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <a href="#form">About Us</a>
                <a href="#form">Contact</a>
                <a href="#form">Privacy Policy</a>
              </div>
              <div className="footer-column">
                <h4>Contact</h4>
                <p><i className="fa-solid fa-phone"></i> <a href="tel:+17204384271">(720) 438-4271</a></p>
                <p><i className="fa-solid fa-envelope"></i> <a href="mailto:info@levelupinsulationco.com">info@levelupinsulationco.com</a></p>
                <p><i className="fa-solid fa-location-dot"></i> 6850 Broadway St, Denver, CO 80221</p>
              </div>
              <div className="footer-column">
                <h4>Hours</h4>
                <p>Mon-Fri: 8:00 AM - 5:00 PM</p>
                <p>Sat: 8:00 AM - 5:00 PM</p>
                <p>Sun: Closed</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Level Up Insulation. All rights reserved.</p>
            <p className="footer-link"><a href="https://levelupinsulationco.com">levelupinsulationco.com</a></p>
          </div>
        </div>
      </footer>

      {/* Floating Phone Button */}
      <a href="tel:+17204384271" className="floating-phone-btn" aria-label="Call us">
        <i className="fa-solid fa-phone"></i>
      </a>
    </div>
  );
}

export default ReplacementPage;

